import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { customersAPI } from '../services/api';
import { Search, UserPlus, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const CustomerSearchModal = ({ isOpen, onClose, onCustomerSelect }) => {
  const queryClient = useQueryClient();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ fullName: '', phoneNumber: '' });

  // Search customers mutation
  const searchCustomersMutation = useMutation(
    (phone) => customersAPI.search(phone),
    {
      onSuccess: (data) => {
        setSearchResults(data.customers || []);
        setIsSearching(false);
      },
      onError: (error) => {
        console.error('Search error:', error);
        setSearchResults([]);
        setIsSearching(false);
        toast.error('Failed to search customers');
      }
    }
  );

  // Create customer mutation
  const createCustomerMutation = useMutation(
    (customerData) => customersAPI.create(customerData),
    {
      onSuccess: (data) => {
        toast.success('Customer created successfully!');
        queryClient.invalidateQueries('customers');
        onCustomerSelect(data.customer);
        onClose();
        setShowAddForm(false);
        setNewCustomer({ fullName: '', phoneNumber: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create customer');
      }
    }
  );

  const handleSearch = async () => {
    if (!phoneNumber.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    searchCustomersMutation.mutate(phoneNumber.trim());
  };

  const handleAddCustomer = () => {
    setNewCustomer({ fullName: '', phoneNumber: phoneNumber });
    setShowAddForm(true);
  };

  const handleCreateCustomer = (e) => {
    e.preventDefault();
    if (!newCustomer.fullName.trim() || !newCustomer.phoneNumber.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    createCustomerMutation.mutate({
      fullName: newCustomer.fullName.trim(),
      phoneNumber: newCustomer.phoneNumber.trim()
    });
  };

  const handleCustomerSelect = (customer) => {
    onCustomerSelect(customer);
    onClose();
    setPhoneNumber('');
    setSearchResults([]);
  };

  const handleClose = () => {
    onClose();
    setPhoneNumber('');
    setSearchResults([]);
    setShowAddForm(false);
    setNewCustomer({ fullName: '', phoneNumber: '' });
  };

  // Search on phone number change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (phoneNumber.trim()) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [phoneNumber]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {showAddForm ? 'Add New Customer' : 'Search Customer'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {!showAddForm ? (
            // Search Form
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter phone number to search..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>
              </div>

              {isSearching && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Searching...</p>
                </div>
              )}

              {!isSearching && phoneNumber.trim() && searchResults.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-700">Found Customers:</h3>
                  {searchResults.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{customer.full_name}</p>
                        <p className="text-sm text-gray-600">{customer.phone_number}</p>
                      </div>
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                  ))}
                </div>
              )}

              {!isSearching && phoneNumber.trim() && searchResults.length === 0 && (
                <div className="text-center py-6">
                  <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No customer found</h3>
                  <p className="text-gray-600 mb-4">
                    No customer with phone number "{phoneNumber}" was found.
                  </p>
                  <button
                    onClick={handleAddCustomer}
                    className="flex items-center justify-center w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add New Customer
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Add Customer Form
            <form onSubmit={handleCreateCustomer} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={newCustomer.fullName}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Enter full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={newCustomer.phoneNumber}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  placeholder="Enter phone number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back to Search
                </button>
                <button
                  type="submit"
                  disabled={createCustomerMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {createCustomerMutation.isLoading ? 'Creating...' : 'Create Customer'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerSearchModal; 